import { FC, RefObject, useEffect, useRef } from 'react'

import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import { GRAY_600 } from 'constants/styling/theme'
import Stack from '@mui/material/Stack'
import { useTranslation } from 'react-i18next'

interface Props {
  rootRef: RefObject<HTMLElement>
  hasMore: boolean
  isLoading: boolean
  onLoadMore: () => void
}

const PRELOAD_MARGIN = '0px 0px 200px 0px'

export const OverviewLoadMore: FC<Props> = ({ rootRef, hasMore, isLoading, onLoadMore }) => {
  const { t } = useTranslation(['chat'])
  const sentinelRef = useRef<HTMLDivElement>(null)
  const onLoadMoreRef = useRef(onLoadMore)
  onLoadMoreRef.current = onLoadMore

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel || !hasMore || isLoading || typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onLoadMoreRef.current()
      },
      { root: rootRef.current, rootMargin: PRELOAD_MARGIN }
    )
    observer.observe(sentinel)

    return () => observer.disconnect()
  }, [hasMore, isLoading, rootRef])

  return (
    <>

      {isLoading &&
        <Stack direction="row" justifyContent="center" padding="0.8rem 0" role="status" aria-label={t('chat:overview.loading_more')}>
          <CircularProgress size={20} sx={{ color: GRAY_600 }} />
        </Stack>
      }

      {hasMore && !isLoading &&
        <Box ref={sentinelRef} data-testid="overview-load-more" height="1px" flexShrink={0} />
      }

    </>
  )
}
